<template>
  <div class="prompt-list">
    <!-- Filters -->
    <div class="filters">
      <select v-model="filterAssistant" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.aiPrompts.allAssistants') }}</option>
        <option value="sparkie_bot">Sparkie Bot</option>
      </select>
      <select v-model="filterUseCase" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.aiPrompts.allUseCases') }}</option>
        <option value="goal_suggestion">{{ $t('bo.aiPrompts.useCaseGoalSuggestion') }}</option>
        <option value="bio_generation">{{ $t('bo.aiPrompts.useCaseBioGeneration') }}</option>
        <option value="matching">{{ $t('bo.aiPrompts.useCaseMatching') }}</option>
        <option value="session_preparation">{{ $t('bo.aiPrompts.useCaseSessionPrep') }}</option>
      </select>
      <select v-model="filterStatus" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.aiPrompts.allStatuses') }}</option>
        <option value="draft">{{ $t('bo.aiPrompts.statusDraft') }}</option>
        <option value="published">{{ $t('bo.aiPrompts.statusPublished') }}</option>
        <option value="archived">{{ $t('bo.aiPrompts.statusArchived') }}</option>
      </select>
      <select v-model="filterLang" @change="onFilterChange" class="filter-select">
        <option value="">{{ $t('bo.aiPrompts.allLanguages') }}</option>
        <option value="auto">{{ $t('bo.aiPrompts.langAuto') }}</option>
        <option value="fr">Français</option>
        <option value="en">English</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>

    <!-- Error -->
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <!-- Empty -->
    <div v-else-if="prompts.length === 0" class="empty">
      {{ $t('bo.aiPrompts.noPrompts') }}
    </div>

    <!-- Table -->
    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.aiPrompts.colName') }}</th>
          <th>{{ $t('bo.aiPrompts.colAssistant') }}</th>
          <th>{{ $t('bo.aiPrompts.colUseCase') }}</th>
          <th>{{ $t('bo.aiPrompts.colProvider') }}</th>
          <th>{{ $t('bo.aiPrompts.colLang') }}</th>
          <th>{{ $t('bo.aiPrompts.colStatus') }}</th>
          <th>{{ $t('bo.aiPrompts.colVersion') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="prompt in prompts" :key="prompt.id">
          <td class="col-name">
            <div class="prompt-name">{{ prompt.name }}</div>
            <div v-if="prompt.internal_notes" class="prompt-notes">{{ truncate(prompt.internal_notes, 60) }}</div>
          </td>
          <td>
            <span class="chip chip-blue">{{ formatAssistant(prompt.assistant) }}</span>
          </td>
          <td>
            <span class="use-case">{{ formatUseCase(prompt.use_case) }}</span>
          </td>
          <td>
            <span class="chip chip-gray">{{ prompt.ai_provider }}</span>
          </td>
          <td>
            <span class="lang-badge">{{ prompt.output_language?.toUpperCase() }}</span>
          </td>
          <td>
            <span :class="['badge', `badge-${prompt.status}`]">{{ $t(`bo.aiPrompts.status${capitalize(prompt.status)}`) }}</span>
          </td>
          <td class="col-version">v{{ prompt.version }}</td>
          <td class="actions">
            <button
              @click="$emit('edit', prompt.id)"
              class="btn-action btn-edit"
              :disabled="actionLoading === prompt.id"
            >{{ $t('bo.common.edit') }}</button>
            <button
              v-if="prompt.status === 'draft'"
              @click="$emit('publish', prompt.id)"
              class="btn-action btn-publish"
              :disabled="actionLoading === prompt.id"
            >{{ $t('bo.aiPrompts.publish') }}</button>
            <button
              v-if="prompt.status !== 'archived'"
              @click="$emit('archive', prompt.id)"
              class="btn-action btn-archive"
              :disabled="actionLoading === prompt.id"
            >{{ $t('bo.aiPrompts.archive') }}</button>
            <button
              @click="$emit('versions', prompt.id)"
              class="btn-action btn-versions"
              :disabled="actionLoading === prompt.id"
            >{{ $t('bo.aiPrompts.versions') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="pagination && pagination.last_page > 1" class="pagination">
      <button
        :disabled="pagination.current_page === 1"
        @click="changePage(pagination.current_page - 1)"
        class="page-btn"
      >&larr;</button>
      <span class="page-info">{{ pagination.current_page }} / {{ pagination.last_page }}</span>
      <button
        :disabled="pagination.current_page === pagination.last_page"
        @click="changePage(pagination.current_page + 1)"
        class="page-btn"
      >&rarr;</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import aiPromptService from '@/services/aiPromptService'

const { t } = useI18n()

const emit = defineEmits(['edit', 'publish', 'archive', 'versions'])

const prompts = ref([])
const loading = ref(false)
const error = ref('')
const filterAssistant = ref('')
const filterUseCase = ref('')
const filterStatus = ref('')
const filterLang = ref('')
const actionLoading = ref(null)
const pagination = ref(null)
const currentPage = ref(1)

const loadPrompts = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = { page: currentPage.value }
    if (filterAssistant.value) params.assistant = filterAssistant.value
    if (filterUseCase.value) params.use_case = filterUseCase.value
    if (filterStatus.value) params.status = filterStatus.value
    if (filterLang.value) params.output_language = filterLang.value
    const res = await aiPromptService.getAll(params)
    prompts.value = res.data?.data?.data || res.data?.data || []
    pagination.value = res.data?.data?.pagination || null
  } catch (e) {
    error.value = t('bo.aiPrompts.failedLoad')
  } finally {
    loading.value = false
  }
}

const onFilterChange = () => {
  currentPage.value = 1
  loadPrompts()
}

const changePage = (page) => {
  currentPage.value = page
  loadPrompts()
}

const truncate = (str, len) => str?.length > len ? str.substring(0, len) + '…' : str

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : ''

const formatAssistant = (val) => {
  const map = { sparkie_bot: 'Sparkie Bot' }
  return map[val] || val
}

const formatUseCase = (val) => {
  const map = {
    goal_suggestion: 'Goal Suggestion',
    bio_generation: 'Bio Generation',
    matching: 'Matching',
    session_preparation: 'Session Prep',
  }
  return map[val] || val
}

defineExpose({ loadPrompts })
onMounted(loadPrompts)
</script>

<style scoped>
.prompt-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.filters {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td {
  border-bottom: none;
}

.col-name {
  max-width: 260px;
}

.prompt-name {
  font-weight: 600;
  color: #1f2937;
}

.prompt-notes {
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.col-version {
  color: #6b7280;
  font-size: 0.8rem;
  white-space: nowrap;
}

.use-case {
  color: #374151;
  font-size: 0.8rem;
}

.chip {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.chip-blue { background: #eff6ff; color: #1d4ed8; }
.chip-gray { background: #f3f4f6; color: #374151; }

.lang-badge {
  display: inline-block;
  padding: 0.15rem 0.4rem;
  background: #f0fdf4;
  color: #15803d;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-draft { background: #fef3c7; color: #92400e; }
.badge-published { background: #d1fae5; color: #065f46; }
.badge-archived { background: #f3f4f6; color: #6b7280; }

.actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.btn-action {
  padding: 0.3rem 0.6rem;
  border-radius: 0.375rem;
  font-size: 0.775rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-edit { border-color: #3b82f6; color: #3b82f6; background: white; }
.btn-edit:hover:not(:disabled) { background: #eff6ff; }

.btn-publish { border-color: #10b981; color: #10b981; background: white; }
.btn-publish:hover:not(:disabled) { background: #ecfdf5; }

.btn-archive { border-color: #f59e0b; color: #f59e0b; background: white; }
.btn-archive:hover:not(:disabled) { background: #fffbeb; }

.btn-versions { border-color: #8b5cf6; color: #8b5cf6; background: white; }
.btn-versions:hover:not(:disabled) { background: #f5f3ff; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f9fafb; }

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
